import apiClient from '../axios';
import {
  ActiveUserPageResponse,
  ActivityScorePageResponse,
  AdminVoteHistoryPageResponse,
  AttendanceConversionStats,
  AttendanceRecordPageResponse,
  AttendanceSummaryItem,
  GetUsersResponse,
  LoginHistoryPageResponse,
  LoginPlatform,
  LoginPlatformStats,
  NotificationConfigItem,
  NotificationHistoryPageResponse,
  NotificationSummaryPageResponse,
  NotificationUserItem,
  ReEngagementDailySummaryItem,
  ReEngagementUserItem,
  TopActiveTargetResponse,
  UserDetailResponse,
  UserChangePageResponse,
  VoteConversionStats,
} from '../types/Admin.api.type';

/**
 * 관리자 관련 API 모음
 *
 * @author hjkim
 */
/* 관리자 API 주소 */
const ADMIN_URL = () => '/api/admin/dashboard';
const INQUIRY_URL = () => '/api/inquiry';

export interface AdminInquiryListItem {
  id: number;
  inquiryType: string;
  content: string;
  authorNickname: string;
  authorEmail: string;
  status: string;
  regDateTime: string;
  replyCount: number;
}

export interface AdminReportListItem {
  id: number;
  questionTitle: string;
  questionId: number;
  reporterNickname: string;
  reporterEmail: string;
  reason: string;
  details: string;
  status: string;
  regDateTime: string;
}

export interface AdminStats {
  totalUsers: number;
  totalQuestions: number;
  totalReports: number;
  pendingReports: number;
  activeUsers: number;
  questionsToday: number;
  totalInquiries: number;
  pendingInquiries: number;
}

export interface MonthlyUserGrowth {
  month: string;
  userCount: number;
}

export interface MonthlySurveyTrend {
  month: string;
  surveyCount: number;
}

export interface RecentActivity {
  type: string;
  message: string;
  timestamp: string;
}

export interface PeriodData<T> {
  today: T[];
  hourly: T[];
  daily: T[];
  weekly: T[];
  monthly: T[];
}

export interface GraphData {
  userGrowth: PeriodData<MonthlyUserGrowth>;
  surveyTrend: PeriodData<MonthlySurveyTrend>;
}

const ADMIN_QUESTION_URL = () => '/api/admin/questions';

export const toggleSurveyPin = async (id: number): Promise<{ message: string; isPinned: boolean }> => {
  const res = await apiClient.patch(`${ADMIN_QUESTION_URL()}/${id}/pin`);
  return res.data;
};

export const updateLinkedRoute = async (
  id: number,
  linkedRoute: string | null,
  linkedRouteLabel: string | null,
): Promise<{ message: string }> => {
  const res = await apiClient.patch(`${ADMIN_QUESTION_URL()}/${id}/linked-route`, { linkedRoute, linkedRouteLabel });
  return res.data;
};

export const adminApi = {
  /**
   * 관리자 통계 조회
   */
  getAdminStats: () => {
    return apiClient.get<AdminStats>(`${ADMIN_URL()}/getAdminStats`);
  },

  /**
   * 유저목록 조회
   */
  getUsers: () => {
    return apiClient.get<GetUsersResponse[]>(`${ADMIN_URL()}/getUsers`);
  },

  /**
   * 관리자용 문의 목록 조회
   * @param status 문의 상태 필터 (선택)
   * @param inquiryType 문의 타입 필터 (선택)
   */
  getAdminInquiries: (status?: string, inquiryType?: string) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (inquiryType) params.append('inquiryType', inquiryType);

    const queryString = params.toString();
    return apiClient.get<AdminInquiryListItem[]>(`${INQUIRY_URL()}/admin${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * 문의 답변 작성 (관리자용)
   * @param inquiryId 문의 ID
   * @param content 답변 내용
   */
  createInquiryReply: (inquiryId: number, content: string) => {
    return apiClient.post(`${INQUIRY_URL()}/reply`, {
      inquiryId,
      content,
    });
  },

  /**
   * 관리자용 신고 목록 조회
   * @param status 신고 상태 필터 (선택)
   */
  getAdminReports: (status?: string) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);

    const queryString = params.toString();
    return apiClient.get<AdminReportListItem[]>(`/api/report/admin${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * 신고 처리 - 질문 삭제
   * @param reportId 신고 ID
   */
  processReportDelete: (reportId: number) => {
    return apiClient.post(`/api/report/admin/${reportId}/delete`);
  },

  /**
   * 신고 기각
   * @param reportId 신고 ID
   */
  processReportReject: (reportId: number) => {
    return apiClient.post(`/api/report/admin/${reportId}/reject`);
  },

  /**
   * 월별 사용자 증가 추이 조회
   */
  getMonthlyUserGrowth: () => {
    return apiClient.get<MonthlyUserGrowth[]>(`${ADMIN_URL()}/getMonthlyUserGrowth`);
  },

  /**
   * 월별 설문 생성 트렌드 조회
   */
  getMonthlySurveyTrend: () => {
    return apiClient.get<MonthlySurveyTrend[]>(`${ADMIN_URL()}/getMonthlySurveyTrend`);
  },

  /**
   * 기간별 사용자 증가 추이 조회
   * @param period 조회 기간 타입 (DAILY, WEEKLY, MONTHLY)
   */
  getUserGrowth: (period: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' = 'DAILY') => {
    return apiClient.get<MonthlyUserGrowth[]>(`${ADMIN_URL()}/getUserGrowth`, {
      params: { period },
    });
  },

  /**
   * 기간별 설문 생성 트렌드 조회
   * @param period 조회 기간 타입 (DAILY, WEEKLY, MONTHLY)
   */
  getSurveyTrend: (period: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' = 'DAILY') => {
    return apiClient.get<MonthlySurveyTrend[]>(`${ADMIN_URL()}/getSurveyTrend`, {
      params: { period },
    });
  },

  /**
   * 그래프 데이터 통합 조회 (사용자 증가 + 설문 트렌드, 모든 기간)
   */
  getGraphData: () => {
    return apiClient.get<GraphData>(`${ADMIN_URL()}/getGraphData`);
  },

  /**
   * 최근 활동 내역 조회
   */
  getRecentActivities: () => {
    return apiClient.get<RecentActivity[]>(`${ADMIN_URL()}/getRecentActivities`);
  },

  /**
   * 로그인 히스토리 조회
   * @param platform 로그인 플랫폼 필터 (선택)
   * @param searchKeyword 검색 키워드 (선택)
   * @param page 페이지 번호 (0부터 시작)
   * @param size 페이지 크기
   */
  getLoginHistories: (
    platform?: LoginPlatform,
    searchKeyword?: string,
    page: number = 0,
    size: number = 20,
    userId?: number,
  ) => {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    if (searchKeyword) params.append('searchKeyword', searchKeyword);
    params.append('page', page.toString());
    params.append('size', size.toString());
    if (userId != null) params.append('userId', userId.toString());

    return apiClient.get<LoginHistoryPageResponse>(`${ADMIN_URL()}/getLoginHistories?${params.toString()}`);
  },

  /**
   * 플랫폼별 로그인 통계 조회
   */
  getLoginPlatformStats: () => {
    return apiClient.get<LoginPlatformStats>(`${ADMIN_URL()}/getLoginPlatformStats`);
  },

  /**
   * 유저 상세 정보 조회
   */
  getUserDetail: (userId: number) => {
    return apiClient.get<UserDetailResponse>(`${ADMIN_URL()}/users/${userId}/detail`);
  },

  /**
   * 출석체크 요약 조회 (사용자별 출석 횟수)
   */
  getAttendanceSummary: () => {
    return apiClient.get<AttendanceSummaryItem[]>(`${ADMIN_URL()}/attendance-summary`);
  },

  /**
   * 유저 변경 이력 조회
   */
  getUserChanges: (userId: number, page: number = 0, size: number = 20) => {
    return apiClient.get<UserChangePageResponse>(`${ADMIN_URL()}/users/${userId}/changes`, {
      params: { page, size },
    });
  },

  /**
   * 활동점수 조회 (페이징)
   * @param page 페이지 번호 (0부터 시작)
   * @param size 페이지 크기
   */
  getActivityScores: (page: number = 0, size: number = 20) => {
    return apiClient.get<ActivityScorePageResponse>(`${ADMIN_URL()}/activity-scores`, {
      params: { page, size },
    });
  },

  /**
   * 개별 출석 기록 조회 (광고 시청 여부 포함)
   */
  getAttendanceRecords: (page: number = 0, size: number = 20) => {
    return apiClient.get<AttendanceRecordPageResponse>(`${ADMIN_URL()}/attendance-records`, {
      params: { page, size },
    });
  },

  /**
   * 어드민 투표 내역 조회
   */
  getVoteHistory: (page: number = 0, size: number = 20, userId?: number) => {
    return apiClient.get<AdminVoteHistoryPageResponse>(`${ADMIN_URL()}/vote-history`, {
      params: { page, size, userId },
    });
  },

  /**
   * 투표 광고 전환율 통계
   */
  getVoteConversionStats: (period: string = 'TODAY') => {
    return apiClient.get<VoteConversionStats>(`${ADMIN_URL()}/vote-conversion`, {
      params: { period },
    });
  },

  /**
   * 출석 광고 전환율 통계
   */
  getAttendanceConversionStats: (period: string = 'TODAY') => {
    return apiClient.get<AttendanceConversionStats>(`${ADMIN_URL()}/attendance-conversion`, {
      params: { period },
    });
  },

  /**
   * 활성 사용자 목록 조회 (출석+투표+로그인 합산 3회 이상)
   */
  getActiveUsers: (page: number = 0, size: number = 20, minCount: number = 3) => {
    return apiClient.get<ActiveUserPageResponse>(`${ADMIN_URL()}/active-users`, {
      params: { page, size, minCount },
    });
  },

  /**
   * 클릭 통계 조회
   */
  getClickStats: (from: string, to: string) => {
    return apiClient.get(`${ADMIN_URL()}/click-stats`, {
      params: { from, to },
    });
  },

  /**
   * 알림 발송 이력 조회
   */
  getNotificationHistory: (page: number = 0, size: number = 20, questionId?: number) => {
    return apiClient.get<NotificationHistoryPageResponse>(`${ADMIN_URL()}/notification-history`, {
      params: { page, size, ...(questionId != null && { questionId }) },
    });
  },

  /**
   * 알림 발송 요약 조회 (설문 단위 그룹핑)
   */
  getNotificationSummary: (page: number = 0, size: number = 20) => {
    return apiClient.get<NotificationSummaryPageResponse>(`${ADMIN_URL()}/notification-summary`, {
      params: { page, size },
    });
  },

  /**
   * 특정 설문의 알림 발송 유저 상세 목록
   */
  getNotificationUsers: (questionId: number) => {
    return apiClient.get<NotificationUserItem[]>(`${ADMIN_URL()}/notification-history/${questionId}/users`);
  },

  // [M-032] 2026-08-03 토스 정책 변경으로 발송 중단.
  // testSendNotification / testSendReEngagement 제거 — BE 엔드포인트도 함께 제거됨.

  verifyUserKey: () => {
    return apiClient.get<Record<string, unknown>>(`${ADMIN_URL()}/verify-user-key`);
  },

  // ── 알림 설정 ──

  getNotificationConfigs: () => {
    return apiClient.get<NotificationConfigItem[]>(`${ADMIN_URL()}/notification-config`);
  },

  updateNotificationConfig: (
    id: number,
    data: Partial<
      Pick<
        NotificationConfigItem,
        'templateCode' | 'enabled' | 'description' | 'sendHour' | 'inactiveDays' | 'cooldownDays' | 'batchSize'
      >
    >,
  ) => {
    return apiClient.put<NotificationConfigItem>(`${ADMIN_URL()}/notification-config/${id}`, data);
  },

  // [M-032] manualSendReEngagement 제거 — 발송 중단

  // ── M-016 활동점수 상위 재접속 알림 ──

  getTopActivePreview: () => {
    return apiClient.get<TopActiveTargetResponse>(`${ADMIN_URL()}/top-active-preview`);
  },

  // [M-032] topActiveManualSend / topActiveTestSend 제거 — 발송 중단

  getTopActiveSummary: () => {
    return apiClient.get<ReEngagementDailySummaryItem[]>(`${ADMIN_URL()}/top-active-summary`);
  },

  getTopActiveUsers: (sentAt: string) => {
    return apiClient.get<ReEngagementUserItem[]>(`${ADMIN_URL()}/top-active-users`, {
      params: { sentAt },
    });
  },

  // ── 재접속 유도 알림 ──

  getReEngagementSummary: () => {
    return apiClient.get<ReEngagementDailySummaryItem[]>(`${ADMIN_URL()}/re-engagement-summary`);
  },

  getReEngagementUsers: (sentAt: string) => {
    return apiClient.get<ReEngagementUserItem[]>(`${ADMIN_URL()}/re-engagement-users`, {
      params: { sentAt },
    });
  },
};

/* ──────────────── Bot API ──────────────── */

const BOT_URL = () => '/api/admin/bot';

export type BotActionType = 'SURVEY_CREATE' | 'COMMENT_REPLY' | 'SKIP' | 'ERROR' | 'FILTER_REJECT' | 'AUTO_DISABLE';

/** S0-29 봇 트랙 — DAILY = 트랙 A(카테고리 순환), EMPATHY = 트랙 B(일상 공감 찬반) */
export type BotTrack = 'DAILY' | 'EMPATHY';

/** 트랙 B 발행 방식 — APPROVAL = 승인 후 발행, AUTO = 자동 발행 */
export type BotPublishMode = 'APPROVAL' | 'AUTO';

/** 트랙별 상태 블록 (spec §4.8-b) */
export interface BotTrackStatus {
  /** 트랙 스위치 (BOT_ENABLED_A / BOT_ENABLED_B) */
  enabled: boolean;
  /** 트랙 발행 시각 (HH:mm, KST) */
  scheduleTime: string;
  /** DAILY 는 null */
  publishMode: BotPublishMode | null;
  todayPosted: boolean;
  /** EMPATHY 만 의미 있음. DAILY 는 0 */
  todayPending: number;
  consecutiveFailureDays: number;
  /** 게시 완료분만 */
  totalSurveys: number;
}

export interface BotStatus {
  enabled: boolean;
  totalSurveys: number;
  totalComments: number;
  consecutiveFailureDays: number;
  lastActivity: string | null;
  /** 일일 설문 자동 생성 시각 (HH:mm, KST) */
  scheduleTime: string;
  /** S0-29 트랙별 상태. 위 enabled 는 마스터(BOT_ENABLED), 나머지 레거시 필드는 DAILY 기준 */
  tracks: Record<BotTrack, BotTrackStatus>;
}

export interface BotScheduleResponse {
  scheduleTime: string;
  message: string;
}

export interface BotToggleResponse {
  enabled: boolean;
  message: string;
}

export interface BotTriggerResponse {
  success: boolean;
  message: string;
}

export interface BotPublishModeResponse {
  /** 400(잘못된 트랙)이면 null */
  mode: BotPublishMode | null;
  message: string;
}

/* ── S0-29 트랙 B 자동발행 대기 타입 ── */

export type BotPendingStatus = 'PENDING' | 'PUBLISHED' | 'REJECTED' | 'EXPIRED';

export interface BotPendingSurveyItem {
  id: number;
  /** yyyy-MM-dd */
  targetDate: string;
  /** 1 = 첫 생성분, 2~ = 다시 만들기 */
  attemptNo: number;
  status: BotPendingStatus;
  description: string;
  options: string[];
  extraContent: string | null;
  /** DB 컬럼이 nullable */
  domain: string | null;
  regDateTime: string;
  /** 승인 게시된 경우만 */
  questionId: number | null;
  /** 마지막 승인 실패 사유 */
  failReason: string | null;
}

export interface BotPendingSummary {
  approved: number;
  rejected: number;
  expired: number;
}

export interface BotPendingList {
  items: BotPendingSurveyItem[];
  summary: BotPendingSummary;
}

/** 승인·반려·다시 만들기 결과. 409/422 에서도 본문은 이 형태 → err.response.data.message */
export interface BotApprovalResult {
  success: boolean;
  questionId: number | null;
  /** 승인 성공(ok)이면 null */
  message: string | null;
  httpStatus: number;
}

export interface BotActivityLogItem {
  id: number;
  actionType: BotActionType;
  detail: string | null;
  questionId: number | null;
  commentId: number | null;
  success: boolean;
  regDateTime: string;
  track: BotTrack;
}

export interface BotTopicHistoryItem {
  id: number;
  topicCategory: string;
  topicKeyword: string;
  questionId: number | null;
  postedDate: string;
  track: BotTrack;
}

export interface BotGeneratedSurvey {
  description: string;
  extraContent: string;
  category: string;
  options: string[];
  topicKeyword: string;
}

export interface BotFeedbackItem {
  id: number;
  content: string;
  active: boolean;
  regDateTime: string;
}

export interface ManualSurveyRequest {
  topic?: string;
  directive?: string;
  optionCount?: number;
  categoryId: number;
  durationMinutes: number;
}

export interface PublishSurveyRequest {
  description: string;
  extraContent?: string;
  categoryId: number;
  options: string[];
  durationMinutes: number;
  topicKeyword?: string;
  referenceType?: string;
  referenceUrl?: string;
  referenceQuestionId?: number;
  referenceLabel?: string;
}

export interface QuestionSearchItem {
  questionId: number;
  description: string;
  categoryId: number;
  isExpired: boolean;
  totalVoteCount: number;
}

/* ── M-026 봇 설문 생성 루틴 타입 ── */

export interface BotSurveyJobCreateRequest {
  /** 뉴스 링크 (개행 구분) */
  sourceUrls: string;
  directive?: string;
  categoryId?: number | null;
  optionCount?: number | null;
}

/** 루틴이 만든 설문 후보 — 🔴 "요약"이 아니라 완성된 설문안이다 */
export interface BotSurveyCandidate {
  topicKeyword?: string;
  description: string;
  extraContent?: string;
  options: string[];
  sourceUrl?: string;
  /** 이 후보를 고른 이유 */
  rationale?: string;
}

export interface BotSurveyJobStatus {
  jobId: number;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'ABANDONED';
  candidates: BotSurveyCandidate[];
  /** 형식이 안 맞아 빠진 후보 수 — 🔴 조용히 사라지면 추적이 안 된다 */
  excludedCount: number | null;
  excludedReasons: string | null;
  errorMessage: string | null;
  attemptCount: number | null;
}

/** 🔴 boolean 만 온다. 토큰 값·길이는 서버가 절대 내려주지 않는다 */
export interface BotSurveyJobHealth {
  agentApiTokenConfigured: boolean;
  routineFireConfigured: boolean;
}

export const botApi = {
  /** 봇 상태 조회 */
  getStatus: () => {
    return apiClient.get<BotStatus>(`${BOT_URL()}/status`);
  },

  /** 봇 ON/OFF 토글 */
  toggle: (enabled: boolean) => {
    return apiClient.put<BotToggleResponse>(`${BOT_URL()}/toggle`, { enabled });
  },

  /** 일일 설문 자동 생성 시각 변경 (HH:mm, KST) */
  updateSchedule: (time: string) => {
    return apiClient.put<BotScheduleResponse>(`${BOT_URL()}/schedule`, { time });
  },

  /* ── S0-29 트랙별 조작 (🔴 트랙 스위치는 마스터 toggle 과 별개) ── */

  /** 트랙 ON/OFF (BOT_ENABLED_A / BOT_ENABLED_B) */
  toggleTrack: (track: BotTrack, enabled: boolean) => {
    return apiClient.put<BotToggleResponse>(`${BOT_URL()}/toggle/${track}`, { enabled });
  },

  /** 트랙 발행 시각 변경 (HH:mm, KST, 06:00~23:00) */
  updateTrackSchedule: (track: BotTrack, time: string) => {
    return apiClient.put<BotScheduleResponse>(`${BOT_URL()}/schedule/${track}`, { time });
  },

  /** 발행 방식 변경 — EMPATHY 전용 (DAILY 는 400) */
  setPublishMode: (track: BotTrack, mode: BotPublishMode) => {
    return apiClient.put<BotPublishModeResponse>(`${BOT_URL()}/publish-mode/${track}`, { mode });
  },

  /** 자동발행 대기 목록 (최근 N일) */
  getPending: (days: number = 14) => {
    return apiClient.get<BotPendingList>(`${BOT_URL()}/pending`, { params: { days } });
  },

  /** 승인하고 발행 */
  approvePending: (id: number) => {
    return apiClient.post<BotApprovalResult>(`${BOT_URL()}/pending/${id}/approve`);
  },

  /** 반려 */
  rejectPending: (id: number) => {
    return apiClient.post<BotApprovalResult>(`${BOT_URL()}/pending/${id}/reject`);
  },

  /** 다시 만들기 (yyyy-MM-dd, 하루 3회까지) */
  regeneratePending: (date: string) => {
    return apiClient.post<BotApprovalResult>(`${BOT_URL()}/pending/regenerate`, null, { params: { date } });
  },

  /** 설문 수동 트리거 */
  triggerSurvey: () => {
    return apiClient.post<BotTriggerResponse>(`${BOT_URL()}/trigger-survey`);
  },

  /** 활동 로그 조회 (타입별 필터 가능) */
  getLogs: (limit: number = 50, actionType?: BotActionType) => {
    const params: Record<string, string | number> = { limit };
    if (actionType) params.actionType = actionType;
    return apiClient.get<BotActivityLogItem[]>(`${BOT_URL()}/logs`, { params });
  },

  /** 주제 히스토리 조회 */
  getTopicHistory: (days: number = 14) => {
    return apiClient.get<BotTopicHistoryItem[]>(`${BOT_URL()}/topic-history`, {
      params: { days },
    });
  },

  /** 미리보기 생성 (AI) */
  generatePreview: (params: ManualSurveyRequest) => {
    return apiClient.post<BotGeneratedSurvey>(`${BOT_URL()}/generate-preview`, params);
  },

  /** 설문 발행 */
  publishSurvey: (params: PublishSurveyRequest) => {
    return apiClient.post<BotTriggerResponse>(`${BOT_URL()}/publish-survey`, params);
  },

  /** 피드백 목록 조회 */
  getFeedbacks: () => {
    return apiClient.get<BotFeedbackItem[]>(`${BOT_URL()}/feedbacks`);
  },

  /** 피드백 추가 */
  addFeedback: (content: string) => {
    return apiClient.post<BotFeedbackItem>(`${BOT_URL()}/feedbacks`, { content });
  },

  /** 피드백 삭제 */
  deleteFeedback: (id: number) => {
    return apiClient.delete(`${BOT_URL()}/feedbacks/${id}`);
  },

  /* ── M-026 봇 설문 생성 루틴 (외부링크 기반 3단계) ── */

  /** job 생성 + 루틴 발사. 🔴 fire 실패해도 job 은 만들어진다(안전망이 건져감) */
  createSurveyJob: (params: BotSurveyJobCreateRequest) => {
    return apiClient.post<{ jobId: number }>(`${BOT_URL()}/survey-job`, params);
  },

  /** 상태 폴링 (3초 간격, 상한 3분) */
  getSurveyJob: (jobId: number) => {
    return apiClient.get<BotSurveyJobStatus>(`${BOT_URL()}/survey-job/${jobId}`);
  },

  /** 화면 이탈/취소 시 즉시 ABANDONED */
  abandonSurveyJob: (jobId: number) => {
    return apiClient.post(`${BOT_URL()}/survey-job/${jobId}/abandon`);
  },

  /** 🔴 토큰 주입 여부(boolean 만). 미주입이면 [분석하기] 를 막는다 */
  getSurveyJobHealth: () => {
    return apiClient.get<BotSurveyJobHealth>(`${BOT_URL()}/survey-job/health`);
  },
};

/* ──────────────── S0-28 어드민 봇 투표 API ────────────────
 * 🔴 임시 조치 — 앱 궤도 진입 시 폐기 예정.
 *    spec: management/features/S0-28_어드민_봇투표/spec.md
 */

export interface BotPersona {
  userId: number;
  /** 어드민 식별용 라벨 (예: "P01 남/20대 초") */
  label: string;
  nickname: string;
  voted: boolean;
  votedOptionId: number | null;
}

export interface BotVoteOption {
  optionId: number;
  optionText: string;
  voteCount: number;
}

export interface BotVoteStatus {
  questionId: number;
  description: string;
  closed: boolean;
  totalVotes: number;
  options: BotVoteOption[];
  personas: BotPersona[];
}

export const botVoteApi = {
  /** 페르소나 봇 목록 (투표 현황 없음) */
  getPersonas: () => {
    return apiClient.get<BotPersona[]>(`${BOT_URL()}/personas`);
  },

  /** 특정 설문의 선택지 + 봇별 투표 현황 */
  getVoteStatus: (questionId: number) => {
    return apiClient.get<BotVoteStatus>(`${BOT_URL()}/vote-status/${questionId}`);
  },

  /** 봇 투표 실행 — 응답은 갱신된 현황 */
  vote: (questionId: number, optionId: number, botUserId: number) => {
    return apiClient.post<BotVoteStatus>(`${BOT_URL()}/vote`, {
      questionId,
      optionId,
      botUserId,
    });
  },
};

/* ──────────────── 진행중 설문 한도 API ──────────────── */

export interface SurveyLimitConfig {
  maxActive: string;
  whitelist: string;
}

/** 진행중 설문 한도 설정 조회 */
export const getSurveyLimit = async (): Promise<SurveyLimitConfig> => {
  const res = await apiClient.get<SurveyLimitConfig>(`${ADMIN_QUESTION_URL()}/survey-limit`);
  return res.data;
};

/** 진행중 설문 한도 설정 저장 — 응답 body 없음(Void), 파싱 금지 */
export const setSurveyLimit = async (body: SurveyLimitConfig): Promise<void> => {
  await apiClient.post(`${ADMIN_QUESTION_URL()}/survey-limit`, body);
};

/** 설문 키워드 검색 (내부 참조 선택용) */
export const searchQuestions = async (keyword: string, limit: number = 20): Promise<QuestionSearchItem[]> => {
  const res = await apiClient.get<QuestionSearchItem[]>(`${ADMIN_QUESTION_URL()}/search`, {
    params: { keyword, limit },
  });
  return res.data;
};

/** 설문 참고 링크 설정 */
export const updateReference = async (
  id: number,
  referenceType: string | null,
  referenceUrl: string | null,
  referenceQuestionId: number | null,
  referenceLabel: string | null,
): Promise<{ message: string }> => {
  const res = await apiClient.patch(`${ADMIN_QUESTION_URL()}/${id}/reference`, {
    referenceType,
    referenceUrl,
    referenceQuestionId: referenceQuestionId?.toString() ?? null,
    referenceLabel,
  });
  return res.data;
};
